import React, { useState, useEffect, useContext } from 'react';
import createAuth0Client, {
  Auth0Client,
  PopupLoginOptions,
  getIdTokenClaimsOptions,
  RedirectLoginOptions,
  GetTokenSilentlyOptions,
  IdToken,
  GetTokenWithPopupOptions,
  LogoutOptions,
  Auth0ClientOptions,
  User,
} from '@auth0/auth0-spa-js';

interface Authentication {
  isAuthenticated: boolean;
  user?: User;
  loading: boolean;
  popupOpen: boolean;
  loginWithPopup(options?: PopupLoginOptions): Promise<void>;
  handleRedirectCallback(): Promise<void>;
  getIdTokenClaims(options?: getIdTokenClaimsOptions): Promise<IdToken>;
  loginWithRedirect(options?: RedirectLoginOptions | undefined): Promise<void>;
  getTokenSilently(options?: GetTokenSilentlyOptions | undefined): Promise<any>;
  getTokenWithPopup(
    options?: GetTokenWithPopupOptions | undefined
  ): Promise<string>;
  logout(options?: LogoutOptions | undefined): void;
}

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

export const Auth0Context = React.createContext<Authentication>(
  {} as Authentication
);
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}: Auth0ClientOptions & {
  children: any;
  onRedirectCallback?: any;
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User>();
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(
        initOptions as Auth0ClientOptions
      );
      setAuth0(auth0FromHook);

      if (window.location.search.includes('code=')) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client!.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client!.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client!.handleRedirectCallback();
    const user = await auth0Client!.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };
  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (opts) => auth0Client!.getIdTokenClaims(opts),
        loginWithRedirect: (opts) => auth0Client!.loginWithRedirect(opts),
        getTokenSilently: (opts) => auth0Client!.getTokenSilently(opts),
        getTokenWithPopup: (opts) => auth0Client!.getTokenWithPopup(opts),
        logout: (opts) => auth0Client!.logout(opts),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
